import { Link } from 'react-router-dom';
import { ErrorField } from "../../../../types/ErrorField";
import SelectTextField, { OptionProps } from "../../../molecules/SelectTextField";
import TextEdit from "../../../molecules/TextEdit";
import { getErrorMessage } from "../../../../utils/formErrorUtils";
import { useEffect, useState } from "react";
import { FaCircleNodes } from 'react-icons/fa6';
import NewTooltip from '../../../atoms/NewTooltip';
import { IoTrashBin } from "react-icons/io5";
import { toast } from 'react-toastify';
import { Auth } from 'aws-amplify';

const PERSON_API_URL = process.env.REACT_APP_PERSON_API_URL;
const API_KEY = process.env.REACT_APP_API_KEY;

interface Props {
    form: any;
    errorFields: ErrorField[];
    changeForm: (field: string, value: string) => void;
    editForm?: boolean;
}

type ConsentingParty = {
    name: string;
    socialId: string;
    uuid: string;
}

const IndividualFormConsentingParty: React.FC<Props> = ({ form, errorFields, changeForm, editForm = false }) => {
    const [consentingPartyRow, setConsentingPartyRow] = useState<ConsentingParty>({
        name: '',
        socialId: '',
        uuid: ''
    });
    const [searchConsenting, setSearchConsenting] = useState('');
    const [searchResults, setSearchResults] = useState<Array<{ value: string; label: string; name: string; socialId: string }>>([]);
    const [consentingList, setConsentingList] = useState<Array<any>>([]);

    useEffect(() => {
        if (form.consentingParty && form.consentingParty.uuid) {
            setConsentingPartyRow({
                name: form.consentingParty.name,
                socialId: form.consentingParty.socialId,
                uuid: form.consentingParty.uuid
            });
        } else {
            setConsentingPartyRow({
                name: '',
                socialId: '',
                uuid: ''
            });
        }
    }, []);

    const handleConsentingSearch = async (searchValue: string) => {
        setSearchConsenting(searchValue);
        
        if (searchValue.length >= 3) {
            try {
                const token = await Auth.currentSession()
                    .then((user) => user.getIdToken().getJwtToken());
                
                const response = await fetch(
                    `${PERSON_API_URL}/person/CONSENTING_PARTY/${searchValue}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'x-api-key': API_KEY || '',
                            'Content-Type': 'application/json'
                        }
                    }
                );
                
                if (response.ok) {
                    const data = await response.json();
                    console.log('🔍 DEBUG - Estrutura da API:', data.results[0]); // DEBUG TEMPORÁRIO
                    const personItems = data.results.map((item: any) => {
                        console.log('🔍 DEBUG - Item:', item); // DEBUG TEMPORÁRIO
                        const document = item.personType === 'INDIVIDUAL' 
                            ? (item.socialId || item.social_id || 'CPF não informado')
                            : (item.govTaxId || item.gov_tax_id || 'CNPJ não informado');
                        return {
                            value: item.uuid,
                            label: `${document} - ${item.name || 'Nome não informado'}`,
                            name: item.name || 'Nome não informado',
                            socialId: document
                        };
                    });
                    setSearchResults(personItems);
                    setConsentingList(data.results);
                } else {
                    toast.error('Erro ao buscar anuente');
                    setSearchResults([]);
                }
            } catch (error) {
                console.error('Erro ao buscar anuente:', error);
                toast.error('Erro ao buscar anuente');
                setSearchResults([]);
            }
        } else {
            setSearchResults([]);
        }
    };

    const handleConsentingPartyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        form.selectedConsentingParty = value;
        changeForm('consentingParty', value);
    }

    const addConsentingParty = () => {
        if (!form.selectedConsentingParty) {
            toast.error("Selecione um anuente.");
            return;
        }
        const found = searchResults.find((r: any) => String(r.value) === String(form.selectedConsentingParty));
        if (found) {
            form.consentingParty = {
                name: found.name,
                socialId: found.socialId,
                uuid: found.value
            }
            setConsentingPartyRow({
                name: found.name,
                socialId: found.socialId,
                uuid: found.value
            });
        }
    }

    const removeConsentingParty = () => {
        form.consentingParty.name = '';
        form.consentingParty.socialId = '';
        form.consentingParty.uuid = '';
        form.selectedConsentingParty = '';
        setConsentingPartyRow({
            name: '',
            socialId: '',
            uuid: ''
        });
        setSearchConsenting('');
        setSearchResults([]);
    }

    return (
        <div className="p-4 m-4">
            {consentingPartyRow.name == '' ? (
                <div className="grid grid-cols-8 gap-4">
                    <div className="col-span-2">
                        <TextEdit
                            id="search-consenting"
                            label="Buscar Anuente"
                            value={searchConsenting}
                            onChange={(e) => handleConsentingSearch(e.target.value)}
                            placeholder="CPF ou Nome (mín. 3 caracteres)"
                        />
                    </div>
                    <SelectTextField
                        id="consentingParty"
                        name='consentingParty'
                        label="Anuente"
                        value={form.selectedConsentingParty}
                        items={searchResults}
                        className="col-span-4"
                        onChange={(e) => handleConsentingPartyChange(e)}
                        errorMessage={getErrorMessage(errorFields, "consentingParty")}
                    />
                    <button
                        id="btn-link-consenting-party"
                        className="bg-cyan-500 text-white px-4 py-2 rounded hover:bg-cyan-800 disabled:bg-gray-300 disabled:text-gray-500 col-span-1 flex items-center gap-2 mt-6"
                        onClick={addConsentingParty}
                        title="Vincular"
                        disabled={form.selectedConsentingParty === '' ? true : false}
                    >
                        <FaCircleNodes style={{ color: 'white' }} />
                        <span>Vincular</span>
                    </button>
                </div>
            ) : (null)}

            <div className="grid grid-cols-12 gap-8">
                {consentingPartyRow.name != '' ? (
                    <table className="border border-gray-300 divide-y divide-gray-200 col-span-8 mt-4">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="py-2 px-4 text-left text-xs font-medium text-gray-500 uppercase border w-7/10">Nome</th>
                                <th className="py-2 px-4 text-left text-xs font-medium text-gray-500 uppercase border w-2/10">CPF</th>
                                <th className="py-2 px-4 text-left text-xs font-medium text-gray-500 uppercase border w-1/10"></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            <tr className='hover:bg-slate-300 hover:shadow-md border-b border border-gray-300'>
                                <td className="py-2 px-4 text-sm text-gray-700" >{consentingPartyRow.name}</td>
                                <td className="py-2 px-4 text-sm text-gray-700" >{consentingPartyRow.socialId}</td>
                                <td>
                                    <div className="flex items-center justify-center  flex items-center">
                                        <NewTooltip content="Remover vinculo">
                                            <Link
                                                id="link-remove-consenting-party"
                                                to='/person/partners/form'
                                                className='text-white bg-red-500 hover:bg-red-700 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded text-sm  text-center'
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    removeConsentingParty();
                                                }}
                                            >
                                                <IoTrashBin className='ml-2 text-2xl text-red-500' />
                                            </Link>
                                        </NewTooltip>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                ) : (null)}
            </div>
        </div>
    );
};

export default IndividualFormConsentingParty;
